/*
  STIC buy-box enhancements. Progressive enhancement only: every feature below has a
  working non-JavaScript fallback, and none of it touches the theme's product form,
  variant logic or cart contract.

  1. Quantity nudge: reveals the "make it two" quick-set button (hidden in markup so
     it is never a dead control without JavaScript) and writes its value into the
     bound quantity input. The input itself is a real <input name="quantity"> that
     posts with the form either way. No price, saving or discount is calculated here,
     because Shopify would not apply one.
  2. Pack tiers write their quantity into the product form's own quantity input.
     (The pack-tier module is gated off in Liquid; the handler stays so the feature
     works untouched if it is ever re-enabled with real multi-product data.)
  3. The sticky buy bar's button submits the real product form by id, by forwarding
     the click to the form's own add-to-cart button, so <product-form>'s AJAX add,
     error handling, cart drawer and analytics all run exactly as normal.

  Idempotent: initialisation is marked per element, so re-running after a Theme
  Editor section reload binds nothing twice.
*/
(function () {
  'use strict';

  var INIT = 'sticPdpInit';

  function quantityInputFor(formId) {
    if (!formId) return null;
    return document.querySelector('input[name="quantity"][form="' + formId + '"]');
  }

  /* ---------- 1. Quantity nudge ---------- */

  function initQuantityNudge(root) {
    if (root.dataset[INIT] === 'true') return;
    root.dataset[INIT] = 'true';

    var button = root.querySelector('[data-stic-qty-set]');
    if (!button) return;

    var targetId = button.getAttribute('data-stic-qty-target');
    var input = targetId ? document.getElementById(targetId) : null;
    if (!input) input = root.querySelector('input[name="quantity"]');
    if (!input) return;

    var nudgeQuantity = parseInt(button.getAttribute('data-stic-qty-set'), 10);
    if (!nudgeQuantity || nudgeQuantity < 2) return;

    var max = parseInt(input.getAttribute('max'), 10);
    if (max && nudgeQuantity > max) return;

    function sync() {
      var current = parseInt(input.value, 10) || 0;
      button.hidden = current >= nudgeQuantity;
    }

    // Only now is the control real, so only now does it become visible.
    sync();

    button.addEventListener('click', function () {
      input.value = String(nudgeQuantity);
      // Let the theme's own listeners (quantity rules, volume pricing) react.
      input.dispatchEvent(new Event('change', { bubbles: true }));
      sync();
      // Move focus to the field the button just changed, so the new value is
      // announced and the shopper can adjust it further.
      input.focus();
    });

    input.addEventListener('input', sync);
    input.addEventListener('change', sync);
  }

  /* ---------- 2. Pack tiers ---------- */

  function initPackTiers(root) {
    if (root.dataset[INIT] === 'true') return;
    root.dataset[INIT] = 'true';

    var formId = root.getAttribute('data-form-id');

    root.addEventListener('change', function (event) {
      var radio = event.target.closest('input[type="radio"][data-quantity]');
      if (!radio || !radio.checked) return;

      var quantity = parseInt(radio.getAttribute('data-quantity'), 10);
      if (!quantity || quantity < 1) return;

      var input = quantityInputFor(formId);
      if (input) {
        input.value = quantity;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /* ---------- 3. Sticky buy bar ---------- */

  function initStickyBar(bar) {
    if (bar.dataset[INIT] === 'true') return;
    bar.dataset[INIT] = 'true';

    var button = bar.querySelector('[data-stic-sticky-submit]');
    if (!button) return;

    button.addEventListener('click', function () {
      var formId = button.getAttribute('form');
      var form = formId ? document.getElementById(formId) : null;
      if (!form) return;

      // Prefer the real add-to-cart button so <product-form>'s own submit
      // handling (AJAX, cart drawer, error states) runs exactly as it does
      // for a normal click.
      var realButton = form.querySelector('[name="add"]');
      if (realButton) {
        realButton.click();
      } else if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    });
  }


  /**
   * Keeps the dispatch countdown honest on a page left open.
   *
   * The server already rendered the correct figure, so this is enhancement only:
   * if it never runs, the line is still right at load. It recomputes against the
   * SHOP's cutoff hour translated through the visitor's own clock offset, ticks
   * once a minute, and removes the line entirely once the cutoff passes rather
   * than counting into negative time or freezing on "0m".
   */
  function initEtaCountdown(el) {
    if (el.dataset.sticEtaBound === 'true') return;
    el.dataset.sticEtaBound = 'true';

    var textEl = el.querySelector('[data-stic-eta-countdown-text]');
    var template = el.getAttribute('data-template') || '';
    var cutoffHour = parseInt(el.getAttribute('data-cutoff-hour'), 10);
    if (!textEl || !template || isNaN(cutoffHour)) return;

    // Anchor to the server's own reading of "minutes until cutoff" so the
    // shopper's device timezone can never shift the deadline. The rendered
    // text is the source of truth; we only decrement from here.
    var deadline = null;

    function parseRendered() {
      var m = textEl.textContent.match(/(?:(\d+)h\s*)?(\d+)m/);
      if (!m) return null;
      var hrs = m[1] ? parseInt(m[1], 10) : 0;
      var mins = parseInt(m[2], 10);
      return Date.now() + (hrs * 60 + mins) * 60000;
    }

    deadline = parseRendered();
    if (deadline === null) return;

    function tick() {
      var msLeft = deadline - Date.now();
      if (msLeft <= 0) {
        clearInterval(timer);
        el.remove();
        return;
      }
      var totalMins = Math.floor(msLeft / 60000);
      var hrs = Math.floor(totalMins / 60);
      var mins = totalMins % 60;
      var label = hrs > 0 ? hrs + 'h ' + mins + 'm' : mins + 'm';
      textEl.textContent = template.replace('[time]', label);
    }

    tick();
    var timer = setInterval(tick, 60000);
    // Without this the interval outlives the element: after the cutoff tick()
    // removes the node and returns, but keeps firing every 60s and retaining
    // the subtree, and every shopify:section:load in the theme editor starts
    // another one alongside it.
    document.addEventListener('shopify:section:unload', function onUnload(event) {
      if (!event.target.contains(el)) return;
      clearInterval(timer);
      document.removeEventListener('shopify:section:unload', onUnload);
    });
  }

  /* The meter's own contract says it is "always rendered inside a region the
     theme re-renders after a cart mutation". On the PDP that is not true: it
     sits inside #ProductInfo and nothing in the cart update path reaches it, so
     after an add the buy box kept quoting the pre-add figure while the drawer
     showed the real one. Refetch this section and swap the element. */
  function initShippingMeter(el) {
    if (el.dataset.sticMeterBound === 'true') return;
    el.dataset.sticMeterBound = 'true';
    if (typeof subscribe !== 'function' || !window.PUB_SUB_EVENTS) return;
    subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () {
      var info = el.closest('product-info') || el.closest('[data-section]');
      var sectionId = info && (info.dataset.section || info.dataset.originalSection);
      if (!sectionId) return;
      fetch(window.location.pathname + '?section_id=' + sectionId)
        .then(function (r) { return r.text(); })
        .then(function (text) {
          var fresh = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('[data-stic-shipping-meter]');
          var live = document.querySelector('[data-stic-shipping-meter]');
          if (fresh && live) {
            live.replaceWith(fresh);
            fresh.dataset.sticMeterBound = 'true';
          }
        })
        .catch(function () {});
    });
  }

  function init(scope) {
    var root = scope || document;
    root.querySelectorAll('[data-stic-qty]').forEach(initQuantityNudge);
    root.querySelectorAll('[data-stic-pack-tiers]').forEach(initPackTiers);
    root.querySelectorAll('[data-stic-sticky-bar]').forEach(initStickyBar);
    root.querySelectorAll('[data-stic-eta-countdown]').forEach(initEtaCountdown);
    root.querySelectorAll('[data-stic-shipping-meter]').forEach(initShippingMeter);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme Editor re-renders sections in place; re-initialise idempotently.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
